import { ChangeDetectorRef, DestroyRef, Directive, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChartConfiguration, ChartType } from 'chart.js';
import { BehaviorSubject, combineLatest } from 'rxjs';

import { SortOrder } from '@shared/services/gql.service';

import { GenericTrialInsightsQuery } from './trial-insights-query.class';
import { TrialInsightsChartModel } from '../models/trial-insights-chart.model';
import { TrialInsightsLegendOptions } from '../models/trial-insights-legend.model';
import {
  TrialInsightsTableModel,
  TrialInsightsTableOptions,
} from '../models/trial-insights-table.model';

export interface BaseConfig {
  color: string;
  exceedMessage?: string;
  title: string;
}

export interface ComponentConfig<CType extends ChartType, LabelType> extends BaseConfig {
  cdr: ChangeDetectorRef;
  chartService: TrialInsightsChartModel<CType, LabelType>;
  queryService: GenericTrialInsightsQuery<CType>;
  tableService: TrialInsightsTableModel;
}

@Directive()
export class GenericTrialInsightsComponent<CType extends ChartType = ChartType, LabelType = unknown>
  implements OnInit
{
  protected readonly destroyRef = inject(DestroyRef);

  cdr: ChangeDetectorRef;
  chartOptions: ChartConfiguration<CType>;
  chartService: TrialInsightsChartModel<CType, LabelType>;
  color = '';
  exceedMessage = '';
  expectedEnrolled = '';
  expectedEnrolledExceeded = false;
  isLoading = false;
  isLoadingRemaining = false;
  legendOptions: TrialInsightsLegendOptions;
  queryService: GenericTrialInsightsQuery<CType>;
  selectedKey: string;
  selectedKey$: BehaviorSubject<string>;
  sortOrder: SortOrder;
  sortOrder$: BehaviorSubject<SortOrder>;
  subscribeToData?: () => void;
  tableOptions: TrialInsightsTableOptions;
  tableService: TrialInsightsTableModel;
  title = '';
  total = '';

  ngOnInit(): void {
    this.subscribeToOptions();
    this.subscribeToLoadingStates();
    this.subscribeToFilters();

    if (this.subscribeToData) {
      this.subscribeToData();
    }
  }

  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(config: ComponentConfig<CType, LabelType>) {
    this.chartService = config.chartService;
    this.tableService = config.tableService;
    this.queryService = config.queryService;
    this.cdr = config.cdr;

    this.title = config.title;
    this.color = config.color;
    this.exceedMessage = config.exceedMessage || '';

    this.tableOptions =
      (config.tableService.createTable && config.tableService.createTable()) ||
      ({} as unknown as TrialInsightsTableOptions);
    this.chartOptions =
      (config.chartService.createChart && config.chartService.createChart()) ||
      ({} as unknown as ChartConfiguration<CType>);
    this.legendOptions =
      (config.chartService.createLegend && config.chartService.createLegend()) ||
      ({} as unknown as TrialInsightsLegendOptions);

    this.selectedKey$ = config.queryService.selectedKey;
    this.sortOrder$ = config.queryService.sortOrder;

    this.selectedKey = this.selectedKey$.getValue();
    this.sortOrder = this.sortOrder$.getValue();
  }

  subscribeToFilters = () => {
    combineLatest([this.selectedKey$, this.sortOrder$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([selectedKey, sortOrder]) => {
        this.selectedKey = selectedKey;
        this.sortOrder = sortOrder;

        this.cdr.markForCheck();
      });
  };

  subscribeToLoadingStates = () => {
    combineLatest([this.queryService.isLoading, this.queryService.isLoadingRemaining])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([isLoading, isLoadingRemaining]) => {
        this.isLoading = isLoading || false;
        this.isLoadingRemaining = isLoadingRemaining || false;

        this.cdr.markForCheck();
      });
  };

  subscribeToOptions = () => {
    combineLatest([
      this.queryService.chartOptions,
      this.queryService.legendOptions,
      this.queryService.tableOptions,
      this.queryService.totalAmount,
      this.queryService.expectedEnrolled,
      this.queryService.expectedEnrolledExceeded,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        ([
          chartOptions,
          legendOptions,
          tableOptions,
          totalAmount,
          expectedEnrolled,
          expectedEnrolledExceeded,
        ]) => {
          this.chartOptions = chartOptions;
          this.legendOptions = legendOptions;
          this.tableOptions = tableOptions;
          this.total = totalAmount;
          this.expectedEnrolled = expectedEnrolled;
          this.expectedEnrolledExceeded = expectedEnrolledExceeded;

          this.cdr.markForCheck();
        }
      );
  };
}
